import { useCallback, useEffect, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Table, { LinkColumn } from "../../components/Table/index";
import Pagination from "../../components/Table/Pagination";
import PageTitle from "../../components/PageTitle/index";
import { DEV_URL, rget } from "../../utils/requests";
import AddHURequestModal from "./AddHURequestModal";
import AddHuBulk from "./AddHuBulk";
import StatusDetailModal from "./StatusDetailModal";
import { useApiFilters } from "../../hooks/useApiFilters";
import { getTablePaginationSizes } from "../../utils/get-table-pagination-sizes";
import { ColumnDef } from "@tanstack/table-core";
import ThreeDotMenu from "../../components/ThreeDotMenu";

interface IHURequest {
  id: number;
  referenceNo: string;
  customerName: string;
  createdAt: string;
  status: string;
  utilityName: string | null;
  totalAccount: number;
}

interface IHURequestApiResponse {
  items: IHURequest[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

const HURequest = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<IHURequest[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const [showAddHURequestModal, setShowAddHURequestModal] = useState<boolean>(false);
  const [showAddHuBulk, setShowAddHuBulk] = useState<boolean>(false);
  const [showStatusDetailModal, setShowStatusDetailModal] = useState<boolean>(false);
  const [selectedHuRequestId, setSelectedHuRequestId] = useState<number | null>(null);

  const { limit, page, setPage, setLimit, orderBy, ascendingOrder, hook } = useApiFilters<IHURequest>({ page: 0, limit: 25 });
  const tableHook = useCallback(() => ({ ...hook(), extra: null }), [hook]);

  const refreshData = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await rget(
          `${DEV_URL}/api/HistoricalUsage/hu-requests?pageNumber=${page + 1}&pageSize=${limit}${orderBy ? `&sortBy=${getSortByField(orderBy)}&sortDirection=${ascendingOrder ? "asc" : "desc"}` : ""}`
        );
        setData(response?.data?.items || []);
        setTotalRecords(response?.data?.totalCount ?? 0);
      } catch (error) {
        console.error("Error fetching HU requests:", error);
        setData([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [limit, page, refreshKey, orderBy, ascendingOrder]);

  const formatDate = (dateValue: string) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const getSortByField = (field?: keyof IHURequest) => {
    switch (field) {
      case "referenceNo":
        return "ReferenceNo";
      case "totalAccount":
        return "TotalAccount";
      case "status":
        return "Status";
      case "createdAt":
        return "CreatedAt";
      default:
        return field ? String(field) : "";
    }
  };

  const ActionColumn = ({ row }: any) => {
    return (
      <ThreeDotMenu>
        <Dropdown.Item onClick={() => navigate(`/account/hu-request/${row.original.id}`)}>
          View
        </Dropdown.Item>
      </ThreeDotMenu>
    );
  };

  const columns: ColumnDef<IHURequest>[] = [
    {
      header: "Reference No",
      accessorKey: "referenceNo",
      cell: (props: any) => (
        <LinkColumn
          value={props.row.original.referenceNo}
          url={`/account/hu-request/${props.row.original.id}`}
          className="text-primary fw-medium"
        />
      ),
    },
    { header: "Total Account", accessorKey: "totalAccount" },
    {
      header: "Status",
      accessorKey: "status",
      cell: (props: any) => {
        const status = props.getValue();
        if (status === "Partial") {
          return (
            <span
              style={{ cursor: "pointer", color: "#0d6efd" }}
              onClick={() => {
                setSelectedHuRequestId(props.row.original.id);
                setShowStatusDetailModal(true);
              }}
            >
              {status}
            </span>
          );
        }
        return status;
      },
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
    {
      header: "Action",
      id: "actions",
      cell: ActionColumn,
      enableSorting: false,
      enableHiding: false,
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1 mt-1">
        <PageTitle title="HU Requests" />
        <div className="d-flex gap-2">
          <Button variant="primary" size="sm" className="d-flex align-items-center gap-1" onClick={() => setShowAddHURequestModal(true)}>
            <i className="uil uil-plus" /> Add HU Request
          </Button>
          <Button variant="primary" size="sm" className="d-flex align-items-center gap-1" onClick={() => setShowAddHuBulk(true)}>
            <i className="uil uil-upload" /> Upload HU
          </Button>
        </div>
      </div>
      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body">
              <div className="hu-request-table-wrapper">
                <Table<IHURequest>
                  columns={columns}
                  data={data}
                  loading={loading}
                  total={totalRecords}
                  hook={tableHook}
                  enableSearch={false}
                  enablePagination={false}
                  enableHideColumns={false}
                  enableSorting={true}
                  enableFilters={false}
                />
              </div>
              <Pagination
                limit={limit}
                page={page}
                setPage={setPage}
                setLimit={setLimit}
                total={totalRecords}
                sizePerPageList={getTablePaginationSizes(totalRecords, true, 25, 50, 100)}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </div>
      <AddHURequestModal show={showAddHURequestModal} onClose={() => setShowAddHURequestModal(false)} onSaved={refreshData} />
      <AddHuBulk show={showAddHuBulk} onClose={() => setShowAddHuBulk(false)} onSaved={refreshData} />
      <StatusDetailModal
        show={showStatusDetailModal}
        huRequestId={selectedHuRequestId}
        onClose={() => { setShowStatusDetailModal(false); setSelectedHuRequestId(null); }}
      />
    </>
  );
};

export default HURequest;
